import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { bust } from "../lib/imgUrl";
import { useSite } from "../context/SiteContext";
import { shouldShowAfterHours } from "../lib/afterhours";

const CHAT_KEY = "camilla_chat_v1";
const SESSION_KEY = "camilla_chat_session";

function newSessionId() {
  return (
    "s-" +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36).slice(-4)
  );
}

function loadInitial() {
  try {
    const raw = localStorage.getItem(CHAT_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function loadSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = newSessionId();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// Simple markdown-ish autolink for wa.me / onlyfans links so replies are clickable.
function renderText(text) {
  const parts = text.split(/(https?:\/\/\S+)/g);
  return parts.map((p, i) => {
    if (/^https?:\/\//.test(p)) {
      const isOf = /onlyfans\.com/i.test(p);
      const isWa = /wa\.me\//i.test(p);
      return (
        <a
          key={i}
          href={p}
          target="_blank"
          rel="noopener noreferrer"
          className="underline decoration-[color:var(--gold)] underline-offset-2 text-gold-2 hover:text-ivory"
          onClick={() => {
            try {
              const a = require("../lib/analytics");
              if (isOf) a.trackOf("chatbot_reply");
              else if (isWa) a.trackWa("chatbot_reply");
            } catch {}
          }}
        >
          {p.replace(/^https?:\/\//, "")}
        </a>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function ChatWidget() {
  const { content, settings, versions } = useSite();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadInitial);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const sessionIdRef = useRef(loadSessionId());
  const listRef = useRef(null);

  const isAfterHours = shouldShowAfterHours(settings.after_hours_mode);
  const [teaserOpen, setTeaserOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("camilla_chat_teaser_seen") === "1") return;
    if (localStorage.getItem("camilla_age_ok") !== "1") return;
    const t = setTimeout(() => setTeaserOpen(true), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!teaserOpen) return;
    const t = setTimeout(() => {
      setTeaserOpen(false);
      localStorage.setItem("camilla_chat_teaser_seen", "1");
    }, 10000);
    return () => clearTimeout(t);
  }, [teaserOpen]);

  useEffect(() => { if (open) setTeaserOpen(false); }, [open]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_KEY, JSON.stringify(messages));
    } catch {}
  }, [messages]);

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    if (open) {
      const t = setTimeout(() => {
        try { document.querySelector('[data-testid="chat-input"]')?.focus(); } catch {}
      }, 320);
      return () => clearTimeout(t);
    }
  }, [messages, open, sending]);

  const clear = () => {
    setMessages([]);
    const newId = newSessionId();
    sessionIdRef.current = newId;
    localStorage.setItem(SESSION_KEY, newId);
    localStorage.setItem(CHAT_KEY, "[]");
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setError("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const res = await api.chat(sessionIdRef.current, next);
      if (res.session_id) sessionIdRef.current = res.session_id;
      setMessages([...next, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setError(e.message || "Something went wrong");
      setMessages(next);
    } finally {
      setSending(false);
    }
  };

  const greeting = isAfterHours
    ? "😴 In bed — sext me on OF"
    : "AI companion · always here for you 💋";

  const subtitleClass = isAfterHours
    ? "text-[color:#e57786]"
    : "text-[rgba(207,198,187,0.5)] text-[10px]";

  const openerSuggestions = [
    "hi camilla 💋",
    "how do bookings work?",
    "what's your 1 hour rate?",
    "are you available tonight?",
  ];

  return (
    <>
      {!open && (
        <>
          <button
            className="chat-fab"
            style={{ opacity: 1 }}
            onClick={() => setOpen(true)}
            data-testid="chat-open-btn"
          >
            <span className="fab-lips" aria-hidden>💋</span>
            <span>Chat with Camilla</span>
          </button>
          {teaserOpen && (
            <div className="chat-teaser" onClick={() => { setTeaserOpen(false); setOpen(true); }} data-testid="chat-teaser">
              hey handsome 💋 come tease me…
              <button
                type="button"
                className="close"
                aria-label="Dismiss"
                onClick={(e) => { e.stopPropagation(); setTeaserOpen(false); localStorage.setItem("camilla_chat_teaser_seen","1"); }}
                data-testid="chat-teaser-close"
              >×</button>
            </div>
          )}
        </>
      )}

      {open && (
        <div
          className="chat-panel fixed z-50 flex flex-col border border-[rgba(201,169,97,0.35)] bg-[#0f0d0c] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]"
          data-testid="chat-panel"
        >
          {/* Mobile drag handle */}
          <div className="sm:hidden pt-2 pb-1 flex justify-center" onClick={() => setOpen(false)} data-testid="chat-drag-handle">
            <div className="w-10 h-1 rounded-full bg-[rgba(201,169,97,0.5)]" />
          </div>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[rgba(201,169,97,0.2)] bg-gradient-to-b from-[#1a1614] to-[#0f0d0c]">
            <div className="wm-wrap w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border border-[rgba(201,169,97,0.4)]">
              <img
                src={bust("/gallery/about.jpg", versions)}
                alt=""
                className="w-full h-full object-cover"
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl text-gold leading-tight">
                Camilla 💋
              </div>
              <div className="flex items-center gap-2 text-[0.72rem]">
                {!isAfterHours && <span className="pulse-dot" />}
                <span className={subtitleClass}>{greeting}</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-gold hover:text-ivory text-xl px-2"
              aria-label="Close chat"
              data-testid="chat-close-btn"
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[280px]"
            data-testid="chat-messages"
          >
            {messages.length === 0 && (
              <div className="text-center text-ivory-dim text-sm py-6">
                <p className="mb-4 font-serif italic text-lg text-gold-2">
                  say hi, handsome 😘
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {openerSuggestions.map((o) => (
                    <button
                      key={o}
                      onClick={() => setInput(o)}
                      className="pill hover:text-ivory transition-colors"
                      data-testid={`chat-opener-${o.slice(0,4)}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[0.92rem] leading-relaxed shadow-[0_10px_30px_-16px_rgba(0,0,0,0.8)] ${
                    m.role === "user"
                      ? "bg-[#3a1219] text-[color:#f4ede4] rounded-br-md"
                      : "bg-[#2a2418] text-[color:var(--gold-2)] rounded-bl-md"
                  }`}
                >
                  {renderText(m.content)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-[#2a2418] text-gold-2 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                  <span className="typing-dot" />
                  <span className="typing-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="typing-dot" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
            {error && (
              <div className="text-xs text-[color:#e57786] text-center" data-testid="chat-error">
                {error}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={send}
            className="p-3 border-t border-[rgba(201,169,97,0.2)] bg-[#0a0a0a] flex gap-2 items-center"
          >
            <input
              type="text"
              className="input-luxe flex-1 !py-2.5"
              placeholder="Ask me anything, babe 💋"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
              autoFocus
              data-testid="chat-input"
            />
            <button
              type="submit"
              className="btn-gold !py-2.5 !px-4 !text-[0.75rem]"
              disabled={sending || !input.trim()}
              data-testid="chat-send-btn"
            >
              Send
            </button>
          </form>
          <div className="px-3 pb-2 text-[10px] text-[color:var(--muted)] flex justify-between">
            <span>AI powered • not a real-time reply</span>
            <button
              type="button"
              onClick={clear}
              className="hover:text-gold underline"
              data-testid="chat-clear-btn"
            >
              Clear chat
            </button>
          </div>
        </div>
      )}
    </>
  );
}
